import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { ProgressBar } from 'primereact/progressbar';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import PageHeader from '../../components/ui/PageHeader';
import { useAuth } from '../../context/AuthContext';
import { ContratoService } from '../../service/ContratoService';
import { CuotaService } from '../../service/CuotaService';
import { exportarExcelHistorialComercial } from '../../utils/excelUtils';

import './ListaContratos.css';

const ListaContratos = () => {
    const { axiosInstance } = useAuth();
    const toast = useRef(null);
    const navigate = useNavigate();

    const [contratos, setContratos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [documentoVisible, setDocumentoVisible] = useState(false);
    const [documentoUrl, setDocumentoUrl] = useState(null);
    const [documentoIsPdf, setDocumentoIsPdf] = useState(false);

    const cerrarDocumentoDialog = () => {
        if (documentoUrl) {
            URL.revokeObjectURL(documentoUrl);
        }
        setDocumentoUrl(null);
        setDocumentoIsPdf(false);
        setDocumentoVisible(false);
    };

    const dt = useRef(null);

    const exportCSV = () => {
        const filtros = {
            estadoContrato: estadoContratoFilter,
            estadoPagos: estadoPagosFilter,
            documento: documentoFilter,
            etapa: etapaFilter,
            manzana: manzanaFilter,
            lote: loteFilter,
            fechaRango: fechaRangoFilter,
            busqueda: globalFilter
        };
        exportarExcelHistorialComercial(filteredContratos, filtros, 'Historial_Comercial');
    };

    const [dialogoLiberarVisible, setDialogoLiberarVisible] = useState(false);
    const [contratoALiberar, setContratoALiberar] = useState(null);
    const [observacionLiberacion, setObservacionLiberacion] = useState('');
    const [liberando, setLiberando] = useState(false);

    const abrirDialogoLiberar = (contrato) => {
        setContratoALiberar(contrato);
        setObservacionLiberacion('');
        setDialogoLiberarVisible(true);
    };

    const confirmarLiberacion = async () => {
        setLiberando(true);
        try {
            await ContratoService.liberarLote(contratoALiberar.id, observacionLiberacion, axiosInstance);
            toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'El contrato ha sido liberado correctamente.' });
            setDialogoLiberarVisible(false);
            cargarContratos();
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: error.response?.data?.error || error.message || 'No se pudo liberar el contrato.' });
        } finally {
            setLiberando(false);
        }
    };

    const ocultarDialogoLiberar = () => {
        setDialogoLiberarVisible(false);
        setContratoALiberar(null);
        setObservacionLiberacion('');
    };

    const footerLiberar = (
        <div>
            <Button label="Cancelar" icon="pi pi-times" onClick={ocultarDialogoLiberar} className="p-button-text" disabled={liberando} />
            <Button label="Confirmar Liberación" icon="pi pi-check" onClick={confirmarLiberacion} className="p-button-danger" loading={liberando} />
        </div>
    );

    // Filter states
    const [globalFilter, setGlobalFilter] = useState('');
    const [estadoContratoFilter, setEstadoContratoFilter] = useState('Todos');
    const [estadoPagosFilter, setEstadoPagosFilter] = useState('Todas las Deudas');
    const [documentoFilter, setDocumentoFilter] = useState('Todos');
    const [fechaRangoFilter, setFechaRangoFilter] = useState(null);
    const [etapaFilter, setEtapaFilter] = useState('Todas las Etapas');
    const [manzanaFilter, setManzanaFilter] = useState('Todas las Manzanas');
    const [loteFilter, setLoteFilter] = useState('Todos los Lotes');

    const limpiarFiltros = () => {
        setGlobalFilter('');
        setEstadoContratoFilter('Todos');
        setEstadoPagosFilter('Todas las Deudas');
        setDocumentoFilter('Todos');
        setFechaRangoFilter(null);
        setEtapaFilter('Todas las Etapas');
        setManzanaFilter('Todas las Manzanas');
        setLoteFilter('Todos los Lotes');
    };

    useEffect(() => {
        cargarContratos();
    }, []);

    const cargarContratos = async () => {
        setLoading(true);
        try {
            const response = await ContratoService.listar(axiosInstance);
            const contratosList = Array.isArray(response) ? response : [];

            const listaFormateada = await Promise.all(contratosList.map(async (item) => {
                let totalPagadoReal = item.abonoInicialReal || item.montoInicial || 0;
                let precioTotalCalculado = item.precioTotal || 1;
                let cuotasList = [];

                try {
                    const cuotasRaw = await CuotaService.listarPorContrato(item.id, axiosInstance);
                    cuotasList = Array.isArray(cuotasRaw) ? cuotasRaw : [];

                    if (cuotasList.length > 0) {
                        totalPagadoReal = cuotasList.reduce((acc, cuota) => acc + (cuota.montoPagado || 0), 0);
                        precioTotalCalculado = item.precioTotal || cuotasList.reduce((acc, cuota) => acc + (cuota.montoTotal || cuota.monto || 0), 0);
                    }
                } catch (error) {
                    console.warn(`No se pudieron cargar cuotas de contrato ${item.id}`, error);
                }

                let totalCuotas = cuotasList.length;
                let cuotasPagadasCount = 0;
                let ultimoPagoFechaObj = null;
                let ultimoPagoMonto = 0;

                if (cuotasList.length > 0) {
                    cuotasList.forEach(c => {
                        const estUpper = (c.estado || '').toUpperCase();
                        if (estUpper === 'PAGADO' || estUpper === 'CANCELADO' || estUpper === 'PAGADO_TOTAL' || c.montoPagado > 0) {
                            // Si monto pagado es el total o parcial, podemos considerarla al menos parcialmente pagada
                            if (estUpper === 'PAGADO' || estUpper === 'CANCELADO' || estUpper === 'PAGADO_TOTAL' || (c.montoPagado > 0 && c.montoPagado >= (c.montoTotal || c.monto))) {
                                cuotasPagadasCount++;
                            }
                            
                            const fechaPagoStr = c.fechaPagoRaw || c.fechaPago || c.fechaPagoFmt || c.fechaRegistro || c.updatedAt;
                            if (fechaPagoStr) {
                                let fp = new Date(fechaPagoStr);
                                if (!isNaN(fp) && (!ultimoPagoFechaObj || fp > ultimoPagoFechaObj)) {
                                    ultimoPagoFechaObj = fp;
                                    ultimoPagoMonto = c.montoPagado || 0;
                                }
                            }
                        }
                    });
                }

                let ultimoPagoFmt = 'Sin pagos';
                if (ultimoPagoFechaObj) {
                    const dia = String(ultimoPagoFechaObj.getDate()).padStart(2, '0');
                    const mes = String(ultimoPagoFechaObj.getMonth() + 1).padStart(2, '0');
                    const anio = ultimoPagoFechaObj.getFullYear();
                    ultimoPagoFmt = `${dia}/${mes}/${anio}`;
                }

                const progresoReal = Math.min(100, Math.round((totalPagadoReal / precioTotalCalculado) * 100));

                // Determinar estado de pago
                let estadoPago = 'AL DIA';
                if (progresoReal >= 100) {
                    estadoPago = 'PAGADO TOTAL';
                } else {
                    let maxDiasAtraso = 0;
                    const tieneAtraso = cuotasList.some(c => {
                        const est = c.estado || '';
                        const estUpper = est.toUpperCase();
                        let currentAtraso = 0;

                        if (c.pagoADestiempo && c.diasRetraso) {
                            currentAtraso = c.diasRetraso;
                        }

                        const fechaVencimientoStr = c.vencimientoRaw || c.fechaVencimiento || c.vencimiento;
                        if (fechaVencimientoStr) {
                            let fecha;
                            if (fechaVencimientoStr.includes('/')) {
                                const [dia, mes, anio] = fechaVencimientoStr.split('/');
                                fecha = new Date(anio, mes - 1, dia);
                            } else if (fechaVencimientoStr.includes('-')) {
                                const [anio, mes, dia] = fechaVencimientoStr.split('T')[0].split('-');
                                fecha = new Date(anio, mes - 1, dia);
                            } else {
                                fecha = new Date(fechaVencimientoStr);
                            }
                            const hoy = new Date();
                            hoy.setHours(0, 0, 0, 0);
                            fecha.setHours(0, 0, 0, 0);
                            const diff = Math.floor((hoy.getTime() - fecha.getTime()) / (1000 * 60 * 60 * 24));
                            if (diff > 0 && (estUpper === 'VENCIDA' || estUpper === 'VENCIDO' || estUpper === 'ATRASADA' || estUpper === 'ATRASADO' || estUpper === 'PENDIENTE' || estUpper === 'PAGADO_PARCIAL')) {
                                currentAtraso = diff;
                            }
                        }

                        if (currentAtraso > maxDiasAtraso) maxDiasAtraso = currentAtraso;

                        return (estUpper === 'VENCIDA' || estUpper === 'VENCIDO' || estUpper === 'ATRASADA' || estUpper === 'ATRASADO' || c.pagoADestiempo || currentAtraso > 0);
                    });

                    if (tieneAtraso) {
                        estadoPago = maxDiasAtraso > 0 ? `VENCIDO (${maxDiasAtraso}d)` : 'PAG. DESTIEMPO';
                    }
                }

                // Determinar tipo inicial
                let tipoInicialFmt = item.tipoInicial || 'TOTAL';
                if (tipoInicialFmt.toUpperCase() === 'PARCIAL') tipoInicialFmt = 'PARCIAL';

                let estadoLoteValue = (item.estadoContrato || '').toUpperCase();
                
                if (!estadoLoteValue) {
                    estadoLoteValue = tipoInicialFmt === 'PARCIAL' ? 'SEPARADO' : 'ACTIVO';
                }

                if (estadoLoteValue === 'VENDIDO' || estadoLoteValue === 'CONVERTIDA_A_CONTRATO' || estadoLoteValue === 'CONVERTIDO' || estadoLoteValue === 'CONTRATO') {
                    estadoLoteValue = 'ACTIVO';
                }

                if (progresoReal >= 100 && (estadoLoteValue === 'ACTIVO' || estadoLoteValue === 'FINALIZADO')) {
                    estadoLoteValue = 'FINALIZADO';
                }

                let alertaSeparacionMsj = null;
                let alertaSeparacionDias = null;
                if (estadoLoteValue === 'SEPARADO') {
                    try {
                        const alertaRes = await ContratoService.obtenerAlertaSeparacion(item.id, axiosInstance);
                        if (alertaRes && alertaRes.mensaje) {
                            alertaSeparacionMsj = alertaRes.mensaje;
                            const matchDias = alertaSeparacionMsj.match(/hace (\d+) días/);
                            if (matchDias) {
                                alertaSeparacionDias = matchDias[1];
                            }
                        }
                    } catch (e) {
                        console.error("Error obteniendo alerta separacion:", e);
                    }
                }

                const documentoFirmadoUrl = typeof item.urlDocumentoFirmado === 'string'
                    ? item.urlDocumentoFirmado.trim()
                    : '';

                return {
                    ...item,
                    id: item.id,
                    codigo: `C-${item.id?.toString().padStart(4, '0')}`,
                    cliente: item.cliente || { nombres: 'Desconocido', apellidos: '', numeroDocumento: 'N/A' },
                    lote: item.lote || { descripcion: 'No asignado' },
                    precioTotal: precioTotalCalculado,
                    totalPagado: totalPagadoReal,
                    estadoLote: estadoLoteValue,
                    fechaEmision: item.fechaRegistro || item.createdAt,
                    fechaEmisionFmt: item.fechaRegistro ? new Date(item.fechaRegistro).toISOString().split('T')[0] : 'N/A',
                    progreso: progresoReal,
                    estadoPago,
                    tieneDocumento: Boolean(documentoFirmadoUrl),
                    tipoInicialFmt,
                    tieneEspeciales: !!item.cuotasEspeciales && item.cuotasEspeciales > 0,
                    alertaSeparacion: alertaSeparacionMsj,
                    alertaSeparacionDias,
                    totalCuotasInfo: totalCuotas > 0 ? `${cuotasPagadasCount} / ${totalCuotas}` : '0 / 0',
                    ultimoPagoFmt,
                    ultimoPagoMonto
                };
            }));

            listaFormateada.sort((a, b) => new Date(b.fechaEmision || 0) - new Date(a.fechaEmision || 0));
            setContratos(listaFormateada);
        } catch (error) {
            console.error(error);
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar los contratos.' });
        } finally {
            setLoading(false);
        }
    };

    // Filter Options
    const estadoContratoOptions = [
        { label: 'Todos', value: 'Todos' },
        { label: 'Activo', value: 'ACTIVO' },
        { label: 'Separado', value: 'SEPARADO' },
        { label: 'Liberado', value: 'LIBERADO' },
        { label: 'Desestimiento', value: 'DESESTIMIENTO' },
        { label: 'Finalizado', value: 'FINALIZADO' }
    ];
    const estadoPagosOptions = [
        { label: 'Todas las Deudas', value: 'Todas las Deudas' },
        { label: 'Al Día', value: 'AL DIA' },
        { label: 'Atrasado', value: 'ATRASADO' },
        { label: 'Cancelado', value: 'CANCELADO' },
        { label: 'Separación Vencida', value: 'SEPARACION_VENCIDA' }
    ];
    const documentoOptions = [{ label: 'Todos', value: 'Todos' }, { label: 'Firmado', value: 'FIRMADO' }, { label: 'Sin Firmar', value: 'SIN_FIRMAR' }];

    const etapaOptions = useMemo(() => {
        const etapas = new Set();
        contratos.forEach(c => {
            const u = c.lote?.manzana?.etapa?.nombre;
            if (u) etapas.add(u);
        });
        return [{ label: 'Todas las Etapas', value: 'Todas las Etapas' }, ...Array.from(etapas).map(u => ({ label: `Etapa ${u}`, value: u }))];
    }, [contratos]);

    const manzanaOptions = useMemo(() => {
        const mzs = new Set();
        contratos.forEach(c => {
            if (etapaFilter !== 'Todas las Etapas' && c.lote?.manzana?.etapa?.nombre !== etapaFilter) return;
            const m = c.lote?.manzana?.nombre;
            if (m) mzs.add(m);
        });
        return [{ label: 'Todas las Manzanas', value: 'Todas las Manzanas' }, ...Array.from(mzs).map(m => ({ label: `Mz ${m}`, value: m }))];
    }, [contratos, etapaFilter]);

    const loteOptions = useMemo(() => {
        const lts = new Set();
        contratos.forEach(c => {
            if (etapaFilter !== 'Todas las Etapas' && c.lote?.manzana?.etapa?.nombre !== etapaFilter) return;
            if (manzanaFilter !== 'Todas las Manzanas' && c.lote?.manzana?.nombre !== manzanaFilter) return;
            const l = c.lote?.numero;
            if (l) lts.add(l);
        });
        return [{ label: 'Todos los Lotes', value: 'Todos los Lotes' }, ...Array.from(lts).map(l => ({ label: `Lote ${l}`, value: l }))];
    }, [contratos, etapaFilter, manzanaFilter]);

    // Apply Filters
    const filteredContratos = useMemo(() => {
        return contratos.filter(item => {
            let match = true;
            if (globalFilter) {
                const search = globalFilter.toLowerCase();
                const cliente = `${item.cliente.nombres} ${item.cliente.apellidos}`.toLowerCase();
                const doc = item.cliente.numeroDocumento?.toLowerCase() || '';
                const code = item.codigo.toLowerCase();
                if (!cliente.includes(search) && !doc.includes(search) && !code.includes(search)) match = false;
            }
            if (estadoContratoFilter !== 'Todos' && item.estadoLote !== estadoContratoFilter) match = false;
            if (estadoPagosFilter !== 'Todas las Deudas') {
                if (estadoPagosFilter === 'ATRASADO' && !item.estadoPago.includes('VENCIDO')) {
                    match = false;
                } else if (estadoPagosFilter === 'CANCELADO' && item.estadoPago !== 'PAGADO TOTAL') {
                    match = false;
                } else if (estadoPagosFilter === 'AL DIA' && item.estadoPago !== 'AL DIA') {
                    match = false;
                } else if (estadoPagosFilter === 'SEPARACION_VENCIDA' && !item.alertaSeparacion) {
                    match = false;
                }
            }
            if (documentoFilter === 'FIRMADO' && !item.tieneDocumento) match = false;
            if (documentoFilter === 'SIN_FIRMAR' && item.tieneDocumento) match = false;
            
            if (etapaFilter !== 'Todas las Etapas' && item.lote?.manzana?.etapa?.nombre !== etapaFilter) match = false;
            if (manzanaFilter !== 'Todas las Manzanas' && item.lote?.manzana?.nombre !== manzanaFilter) match = false;
            if (loteFilter !== 'Todos los Lotes' && item.lote?.numero !== loteFilter) match = false;

            if (fechaRangoFilter && fechaRangoFilter[0]) {
                const itemDate = new Date(item.fechaEmision);
                itemDate.setHours(0, 0, 0, 0);
                
                const start = new Date(fechaRangoFilter[0]);
                start.setHours(0, 0, 0, 0);
                
                let end = null;
                if (fechaRangoFilter[1]) {
                    end = new Date(fechaRangoFilter[1]);
                    end.setHours(23, 59, 59, 999);
                }

                if (itemDate < start) match = false;
                if (end && itemDate > end) match = false;
            }
            return match;
        });
    }, [contratos, globalFilter, estadoContratoFilter, estadoPagosFilter, etapaFilter, manzanaFilter, loteFilter, documentoFilter, fechaRangoFilter]);

    // KPIs
    const kpis = useMemo(() => {
        const activos = contratos.length;
        const vendido = contratos.reduce((acc, c) => acc + c.precioTotal, 0);
        const recaudado = contratos.reduce((acc, c) => acc + c.totalPagado, 0);
        const progresoGlobal = vendido > 0 ? (recaudado / vendido) * 100 : 0;
        const atrasos = contratos.filter(c => c.estadoPago.includes('DESTIEMPO') || c.estadoPago.includes('VENCIDO')).length;
        return { activos, vendido, recaudado, progresoGlobal, atrasos };
    }, [contratos]);

    // Templates
    const contratoTemplate = (row) => (
        <div className="flex align-items-center gap-2">
            <div className="flex align-items-center justify-content-center border-round text-blue-500 bg-blue-50" style={{ width: '32px', height: '32px' }}>
                <i className="pi pi-file" />
            </div>
            <div className="flex flex-column">
                <span className="font-bold text-800">{row.codigo}</span>
                <span className="text-xs text-500 flex align-items-center gap-1 mt-1"><i className="pi pi-calendar" style={{ fontSize: '0.7rem' }}></i> {row.fechaEmisionFmt}</span>
            </div>
        </div>
    );

    const clienteTemplate = (row) => (
        <div className="flex flex-column">
            <span className="font-bold text-800 text-sm">{row.cliente.nombres} {row.cliente.apellidos}</span>
            <span className="text-xs text-500 flex align-items-center gap-1 mt-1"><i className="pi pi-id-card text-400" style={{ fontSize: '0.7rem' }}></i> Doc: {row.cliente.numeroDocumento}</span>
        </div>
    );

    const inmuebleTemplate = (row) => {
        const mz = row.lote?.manzana?.nombre || '';
        const loteNum = row.lote?.numero || '';
        const urb = row.lote?.manzana?.etapa?.urbanizacion?.nombre || '';
        const etapa = row.lote?.manzana?.etapa?.nombre || '';
        return (
            <div className="flex flex-column gap-1">
                <div className="flex align-items-center gap-2 text-700 font-bold text-sm">
                    <i className="pi pi-map-marker text-400"></i>
                    <span>Mz {mz} - Lote {loteNum}</span>
                </div>
                <div className="flex align-items-center gap-1">
                    <span className="text-xs text-500 border-1 surface-border px-2 py-1 border-round-sm bg-gray-50 flex align-items-center gap-1">
                        <i className="pi pi-building text-300" style={{ fontSize: '0.7rem' }}></i> Urb. {urb} - Etapa {etapa}
                    </span>
                </div>
            </div>
        );
    };

    const estadoDocTemplate = (row) => {
        let tagClass = 'dt-tag-activo';
        if (row.estadoLote === 'SEPARADO') tagClass = 'dt-tag-separado';
        else if (row.estadoLote === 'LIBERADO' || row.estadoLote === 'DESESTIMIENTO') tagClass = 'dt-tag-destiempo';
        else if (row.estadoLote === 'FINALIZADO') tagClass = 'dt-tag-pagado-total';

        return (
            <div className="flex flex-column align-items-center gap-2">
                <span className={`dt-tag ${tagClass}`}>{row.estadoLote}</span>
                <div className={`flex align-items-center justify-content-center border-round px-2 py-1 ${row.tieneDocumento ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    <i className={row.tieneDocumento ? "pi pi-file-check mr-1" : "pi pi-file-excel mr-1"} style={{ fontSize: '0.8rem' }}></i>
                    <span className="text-xs font-bold" style={{ fontSize: '0.7rem' }}>{row.tieneDocumento ? 'Firmado' : 'Sin Doc'}</span>
                </div>
            </div>
        );
    };

    const precioVentaTemplate = (row) => (
        <span className="font-black text-800">S/ {row.precioTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
    );

    const recaudadoTemplate = (row) => (
        <span className="font-black text-green-600">S/ {row.totalPagado.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
    );

    const progresoEstadoTemplate = (row) => {
        let estadoBadge;
        if (row.estadoPago === 'PAGADO TOTAL') {
            estadoBadge = <span className="dt-tag-pagado-total" style={{ fontSize: '0.7rem', padding: '0.2rem 0.5rem' }}>PAGADO TOTAL</span>;
        } else if (row.estadoPago.includes('VENCIDO') || row.estadoPago.includes('DESTIEMPO')) {
            estadoBadge = <span className="dt-tag-destiempo" style={{ fontSize: '0.7rem', padding: '0.2rem 0.5rem' }}><i className="pi pi-exclamation-triangle text-xs mr-1"></i> {row.estadoPago}</span>;
        } else {
            estadoBadge = <span className="dt-tag-bordered dt-tag-aldia" style={{ fontSize: '0.7rem', padding: '0.2rem 0.5rem' }}>AL DIA</span>;
        }

        const colorBarra = row.estadoPago.includes('VENCIDO') ? 'var(--red-500)' : 'var(--green-500)';

        return (
            <div className="flex flex-column align-items-center gap-2" style={{ minWidth: '90px', margin: '0 auto' }}>
                {estadoBadge}
                <div className="w-full mt-1">
                    <div className="flex justify-content-center align-items-center mb-1">
                        <span className="text-xs font-bold text-600">{row.progreso}% Pagado</span>
                    </div>
                    <ProgressBar value={row.progreso} displayValueTemplate={() => ''} style={{ height: '6px', width: '100%', borderRadius: '4px' }} color={colorBarra}></ProgressBar>
                </div>
                {row.alertaSeparacion && (
                    <div className="mt-2 flex justify-content-center w-full" title={row.alertaSeparacion}>
                        <div className="bg-red-50 border-1 border-red-300 text-red-700 font-bold border-round px-2 py-1 flex flex-column align-items-center w-full shadow-1">
                            <span className="flex align-items-center gap-1" style={{ fontSize: '0.7rem' }}>
                                <i className="pi pi-exclamation-triangle"></i>
                                SEP. VENCIDA
                            </span>
                            <span className="text-red-900 mt-1" style={{ fontSize: '0.8rem' }}>
                                Hace {row.alertaSeparacionDias ? `${row.alertaSeparacionDias} días` : 'varios días'}
                            </span>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const abrirVisorDocumento = async (row) => {
        if (!row.tieneDocumento) {
            toast.current.show({ severity: 'warn', summary: 'Atención', detail: 'No hay documento firmado asociado al contrato.' });
            return;
        }

        try {
            const blob = await ContratoService.descargarDocumentoFirmado(row.id, axiosInstance);
            const url = URL.createObjectURL(blob);
            setDocumentoUrl(url);
            setDocumentoIsPdf((blob?.type || '').toLowerCase().includes('pdf'));
            setDocumentoVisible(true);
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo cargar el documento.' });
        }
    };

    const isPdf = () => documentoIsPdf;

    const accionesTemplate = (row) => (
        <div className="flex align-items-center justify-content-center gap-4 dt-actions-group">
            <Button icon="pi pi-file-pdf" className="p-button-text p-button-info p-button-rounded text-3xl" onClick={() => abrirVisorDocumento(row)} tooltip="Ver Documento" tooltipOptions={{ position: 'top' }} />
            <Button label="Ver Detalle" className="btn-primary-custom shadow-2 border-round-xl font-bold" onClick={() => navigate(`/detalle_contrato/${row.id}`)} />
            {row.estadoContrato !== 'LIBERADO' && (
                <Button icon="pi pi-unlock" className="p-button-text p-button-danger p-button-rounded text-3xl" onClick={() => abrirDialogoLiberar(row)} tooltip="Liberar Contrato" tooltipOptions={{ position: 'top' }} />
            )}
        </div>
    );

    return (
        <div className="listacontratos-premium">
            <Toast ref={toast} />

            <Dialog header={<><i className="pi pi-unlock text-red-500 mr-2"></i>Liberar Contrato</>} visible={dialogoLiberarVisible} style={{ width: '450px' }} onHide={ocultarDialogoLiberar} modal footer={footerLiberar}>
                <div className="flex flex-column gap-3 mt-2">
                    <p className="m-0 text-700">
                        ¿Está seguro que desea liberar el contrato <b>{contratoALiberar?.codigo}</b>?
                        El lote volverá a estar <b>DISPONIBLE</b> y este contrato se marcará como <b>LIBERADO</b>.
                    </p>
                    <div className="flex flex-column gap-2 mt-2">
                        <label htmlFor="observacionLib" className="font-bold text-700">Observación (Opcional)</label>
                        <InputTextarea 
                            id="observacionLib"
                            value={observacionLiberacion} 
                            onChange={(e) => setObservacionLiberacion(e.target.value)} 
                            rows={3} 
                            placeholder="Ej. Liberación de lote por falta de pago..." 
                            className="w-full"
                        />
                    </div>
                </div>
            </Dialog>

            <Dialog header={<><i className="pi pi-file-pdf text-blue-500 mr-2"></i>Documento Firmado</>} visible={documentoVisible} style={{ width: 'min(900px, 95vw)', height: '80vh' }} onHide={cerrarDocumentoDialog} modal>
                {documentoUrl && (
                    <div className="flex justify-content-center align-items-center h-full border-round overflow-hidden">
                        {isPdf() ? (
                            <iframe src={documentoUrl} title="Documento" className="w-full h-full border-none" style={{ minHeight: '65vh' }} />
                        ) : (
                            <img src={documentoUrl} alt="Documento" style={{ maxWidth: '100%', maxHeight: '65vh', objectFit: 'contain' }} />
                        )}
                    </div>
                )}
            </Dialog>

            {/* Header Area */}
            <div className="flex flex-column md:flex-row justify-content-between align-items-center mb-4 gap-3">
                <div className="flex align-items-center gap-3">
                    <div className="bg-blue-600 text-white p-3 border-round-xl shadow-2 flex align-items-center justify-content-center">
                        <i className="pi pi-history text-2xl"></i>
                    </div>
                    <div>
                        <h1 className="text-3xl font-black text-800 m-0">Historial Comercial</h1>
                        <p className="text-500 text-sm mt-1 m-0 font-medium">Monitorea ventas, cobranzas y el estado de la documentación de tus clientes.</p>
                    </div>
                </div>
                <div className="flex gap-2">
                    <Button label="Exportar" icon="pi pi-download" className="p-button-outlined p-button-secondary border-round-xl font-bold bg-white" onClick={exportCSV} />
                    <Button label="Nuevo Contrato" icon="pi pi-plus" className="btn-primary-custom shadow-2 border-round-xl font-bold" onClick={() => navigate('/contrato/nuevo')} />
                </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-nogutter gap-3 mb-4">
                <div className="col flex-1">
                    <div className="kpi-card surface-0 p-4 border-round-2xl shadow-1 flex align-items-center gap-4">
                        <div className="kpi-icon bg-blue-50 text-blue-500 border-round-lg flex align-items-center justify-content-center p-3">
                            <i className="pi pi-file-check text-2xl"></i>
                        </div>
                        <div>
                            <p className="text-xs font-bold text-500 uppercase m-0 mb-1 tracking-widest">Contratos Activos</p>
                            <div className="flex align-items-baseline gap-2">
                                <span className="text-3xl font-black text-800">{kpis.activos}</span>
                                <span className="text-xs text-500 font-medium">Operaciones</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col flex-1">
                    <div className="kpi-card surface-0 p-4 border-round-2xl shadow-1 flex align-items-center gap-4">
                        <div className="kpi-icon bg-teal-50 text-teal-500 border-round-lg flex align-items-center justify-content-center p-3">
                            <i className="pi pi-chart-line text-2xl"></i>
                        </div>
                        <div>
                            <p className="text-xs font-bold text-500 uppercase m-0 mb-1 tracking-widest">Total Vendido</p>
                            <div className="flex align-items-baseline gap-2">
                                <span className="text-2xl font-black text-800">S/ {kpis.vendido.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col flex-1">
                    <div className="kpi-card surface-0 p-4 border-round-2xl shadow-1 flex align-items-center gap-4 border-left-3 border-purple-500">
                        <div className="kpi-icon bg-purple-50 text-purple-500 border-round-lg flex align-items-center justify-content-center p-3">
                            <i className="pi pi-wallet text-2xl"></i>
                        </div>
                        <div>
                            <p className="text-xs font-bold text-500 uppercase m-0 mb-1 tracking-widest">Total Recaudado</p>
                            <span className="text-2xl font-black text-purple-600 block mb-1">S/ {kpis.recaudado.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                            <span className="bg-green-100 text-green-700 text-xs font-bold px-2 py-1 border-round-sm">Avance: {kpis.progresoGlobal.toFixed(0)}%</span>
                        </div>
                    </div>
                </div>

                <div className="col flex-1">
                    <div className="kpi-card surface-0 p-4 border-round-2xl shadow-1 flex align-items-center gap-4 border-1 border-red-100 relative overflow-hidden">
                        <div className="absolute top-0 right-0 bg-red-50" style={{ width: '5rem', height: '5rem', borderBottomLeftRadius: '100%', zIndex: 0 }}></div>
                        <div className="kpi-icon bg-red-100 text-red-500 border-round-lg flex align-items-center justify-content-center p-3 relative z-1">
                            <i className="pi pi-exclamation-circle text-2xl"></i>
                        </div>
                        <div className="relative z-1">
                            <p className="text-xs font-bold text-red-400 uppercase m-0 mb-1 tracking-widest">Atrasos de Pago</p>
                            <div className="flex align-items-baseline gap-2">
                                <span className="text-3xl font-black text-red-600">{kpis.atrasos}</span>
                                <span className="text-xs text-red-400 font-bold border-1 border-red-200 px-2 py-1 border-round-sm bg-white">Clientes</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filter Panel */}
            <div className="surface-0 p-4 border-round-2xl shadow-1 mb-4">
                <div className="flex flex-column md:flex-row justify-content-between align-items-center mb-3">
                    <div className="flex align-items-center gap-3">
                        <h3 className="m-0 text-lg font-bold text-700 flex align-items-center gap-2">
                            <i className="pi pi-filter text-blue-500"></i> Filtros de Búsqueda
                        </h3>
                        <Button label="Limpiar" icon="pi pi-filter-slash" className="p-button-text p-button-secondary p-button-sm font-bold" onClick={limpiarFiltros} tooltip="Restablecer filtros" />
                    </div>
                    <span className="p-input-icon-left w-full md:w-25rem shadow-1 border-round-xl mt-3 md:mt-0">
                        <i className="pi pi-search ml-2" />
                        <InputText value={globalFilter} onChange={(e) => setGlobalFilter(e.target.value)} placeholder="Buscar cliente, DNI o N° contrato..." className="w-full bg-white border-1 surface-border border-round-xl p-3 pl-5" />
                    </span>
                </div>

                <div className="grid grid-nogutter gap-3 filter-grid">
                    <div className="col">
                        <label className="block text-xs font-bold text-600 mb-2 uppercase">Est. Contrato</label>
                        <Dropdown value={estadoContratoFilter} options={estadoContratoOptions} onChange={(e) => setEstadoContratoFilter(e.value)} className="w-full bg-white border-1 surface-border shadow-1 border-round-lg font-bold" />
                    </div>
                    <div className="col">
                        <label className="block text-xs font-bold text-600 mb-2 uppercase">Est. Pagos</label>
                        <Dropdown value={estadoPagosFilter} options={estadoPagosOptions} onChange={(e) => setEstadoPagosFilter(e.value)} className="w-full bg-white border-1 surface-border shadow-1 border-round-lg font-bold" />
                    </div>
                    <div className="col">
                        <label className="block text-xs font-bold text-600 mb-2 uppercase">Documento</label>
                        <Dropdown value={documentoFilter} options={documentoOptions} onChange={(e) => setDocumentoFilter(e.value)} className="w-full bg-white border-1 surface-border shadow-1 border-round-lg font-bold" />
                    </div>
                    <div className="col">
                        <label className="block text-xs font-bold text-600 mb-2 uppercase">Fecha Emisión</label>
                        <Calendar value={fechaRangoFilter} onChange={(e) => setFechaRangoFilter(e.value)} placeholder="Desde - Hasta" selectionMode="range" dateFormat="dd/mm/yy" className="w-full shadow-1 border-round-lg calendar-custom font-bold" showIcon />
                    </div>
                </div>

                <div className="grid grid-nogutter gap-3 mt-3 filter-grid">
                    <div className="col">
                        <label className="block text-xs font-bold text-600 mb-2 uppercase">Etapa</label>
                        <Dropdown value={etapaFilter} options={etapaOptions} onChange={(e) => { setEtapaFilter(e.value); setManzanaFilter('Todas las Manzanas'); setLoteFilter('Todos los Lotes'); }} className="w-full bg-white border-1 surface-border shadow-1 border-round-lg font-bold" filter />
                    </div>
                    <div className="col">
                        <label className="block text-xs font-bold text-600 mb-2 uppercase">Manzana</label>
                        <Dropdown value={manzanaFilter} options={manzanaOptions} onChange={(e) => { setManzanaFilter(e.value); setLoteFilter('Todos los Lotes'); }} className="w-full bg-white border-1 surface-border shadow-1 border-round-lg font-bold" filter />
                    </div>
                    <div className="col">
                        <label className="block text-xs font-bold text-600 mb-2 uppercase">Lote</label>
                        <Dropdown value={loteFilter} options={loteOptions} onChange={(e) => setLoteFilter(e.value)} className="w-full bg-white border-1 surface-border shadow-1 border-round-lg font-bold" filter />
                    </div>
                    <div className="col"></div>
                </div>
            </div>

            {/* DataTable */}
            <div className="surface-0 border-round-2xl shadow-1 overflow-hidden">
                <DataTable
                    ref={dt}
                    value={filteredContratos}
                    paginator
                    rows={7}
                    loading={loading}
                    className="premium-table"
                    stripedRows={false}
                    emptyMessage="No se encontraron contratos con estos filtros."
                    exportFilename="Contratos"
                    paginatorTemplate="CurrentPageReport PrevPageLink PageLinks NextPageLink"
                    currentPageReportTemplate="Mostrando {last} contratos"
                >
                    <Column header="1. CONTRATO" body={contratoTemplate} style={{ minWidth: '150px' }} />
                    <Column header="2. CLIENTE" body={clienteTemplate} style={{ minWidth: '220px' }} />
                    <Column header="3. INMUEBLE" body={inmuebleTemplate} style={{ minWidth: '250px' }} />
                    <Column header="4. ESTADO" body={estadoDocTemplate} style={{ minWidth: '120px', textAlign: 'center' }} />
                    <Column header="5. PRECIO VENTA" body={precioVentaTemplate} style={{ minWidth: '130px', textAlign: 'center', fontWeight: 'bold' }} />
                    <Column header="6. RECAUDADO" body={recaudadoTemplate} style={{ minWidth: '130px', textAlign: 'center', fontWeight: 'bold', backgroundColor: 'var(--green-50)' }} />
                    <Column header="7. PROGRESO PAGO" body={progresoEstadoTemplate} style={{ minWidth: '140px', textAlign: 'center' }} />
                    <Column header="8. ACCIONES" body={accionesTemplate} style={{ minWidth: '130px', textAlign: 'center' }} />
                </DataTable>
            </div>
        </div>
    );
};

export default ListaContratos;